import React from "react";
import {
  Box,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Avatar,
  Card,
  CardContent,
  Paper,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { styled } from "@mui/material/styles";
import Israel from "../../assets/images/traders/Israel.png";
import Adewumi from "../../assets/images/traders/Adewumi.png";
// Import slick carousel
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// ============================================================
// STYLED COMPONENTS
// ============================================================

const FeatureListItem = styled(ListItem)({
  backgroundColor: "rgba(0, 0, 0, 0.04)",
  borderRadius: "10px",
  marginBottom: "10px",
  padding: "12px 20px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",

  "&:hover": {
    backgroundColor: "rgba(10, 79, 213, 0.08)",
    transform: "translateX(6px)",
    boxShadow: "0 4px 15px rgba(10, 79, 213, 0.12)",
  },

  "&:active": {
    transform: "translateX(6px) scale(0.98)",
  },
});

const TraderCard = styled(Card)({
  borderRadius: "16px",
  padding: "28px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  height: "420px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  border: "1px solid #eef2f7",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  margin: "0 10px",

  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 15px 45px rgba(10, 79, 213, 0.15)",
    borderColor: "rgba(10, 79, 213, 0.3)",
  },
});

const StyledAvatar = styled(Avatar)({
  width: 72,
  height: 72,
  marginRight: 16,
  border: "3px solid #ffffff",
  boxShadow: "0 6px 20px rgba(10, 79, 213, 0.2)",
  transition: "all 0.4s ease",

  "&:hover": {
    transform: "scale(1.08) rotate(5deg)",
    boxShadow: "0 10px 30px rgba(10, 79, 213, 0.35)",
  },
});

const VipButton = styled(Button)({
  backgroundColor: "#0a4fd5",
  color: "#ffffff",
  padding: "14px 42px",
  borderRadius: "10px",
  fontWeight: 600,
  fontSize: "16px",
  textTransform: "none",
  boxShadow: "0 6px 20px rgba(10, 79, 213, 0.3)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    backgroundColor: "#083eaa",
    transform: "translateY(-3px)",
    boxShadow: "0 10px 30px rgba(10, 79, 213, 0.4)",
  },

  "&:active": {
    transform: "translateY(-1px) scale(0.98)",
  },
});

// ============================================================
// CUSTOM ARROWS
// ============================================================

const CustomNextArrow = (props) => {
  const { onClick } = props;
  return (
    <Box
      onClick={onClick}
      sx={{
        position: "absolute",
        top: "50%",
        right: { xs: -10, md: -25 },
        transform: "translateY(-50%)",
        zIndex: 2,
        width: 40,
        height: 40,
        borderRadius: "50%",
        backgroundColor: "#0a4fd5",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        boxShadow: "0 4px 15px rgba(10, 79, 213, 0.35)",
        transition: "all 0.3s ease",

        "&:hover": {
          backgroundColor: "#083eaa",
          transform: "translateY(-50%) scale(1.1)",
        },
      }}
    >
      <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
    </Box>
  );
};

const CustomPrevArrow = (props) => {
  const { onClick } = props;
  return (
    <Box
      onClick={onClick}
      sx={{
        position: "absolute",
        top: "50%",
        left: { xs: -10, md: -25 },
        transform: "translateY(-50%)",
        zIndex: 2,
        width: 40,
        height: 40,
        borderRadius: "50%",
        backgroundColor: "#0a4fd5",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        boxShadow: "0 4px 15px rgba(10, 79, 213, 0.35)",
        transition: "all 0.3s ease",

        "&:hover": {
          backgroundColor: "#083eaa",
          transform: "translateY(-50%) scale(1.1)",
        },
      }}
    >
      <ArrowBackIosIcon sx={{ fontSize: 16, ml: 0.5 }} />
    </Box>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const ProfessionalTradersSection = () => {
  // Slick carousel settings
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    nextArrow: <CustomNextArrow />,
    prevArrow: <CustomPrevArrow />,
    customPaging: (i) => (
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: "#0a4fd5",
          opacity: 0.4,
          transition: "all 0.3s ease",
          "&:hover": {
            opacity: 0.8,
          },
        }}
      />
    ),
    dotsClass: "slick-dots custom-dots",
  };

  const traders = [
    {
      name: "Oluwasegun Israel",
      experience: "Lead Trader",
      testimony:
        "To excel in the markets, I focus on moving with their natural rhythm. Rather than resisting market trends, I embrace them, finding opportunities where the path is clear and less obstructed. By carefully observing market data and understanding the broader economic landscape, I leverage precise technical analysis to uncover the best moments to enter trades. This method allows me to stay ahead of the curve and optimize my trading success.",
      img: Israel,
    },
    {
      name: "Adewumi Grace",
      experience: "VIP Signals Manager",
      testimony:
        "Long-term trading success hinges on a deep understanding of market fundamentals. Without insight into what drives market movements, achieving consistent profitability is nearly impossible. My expertise lies in global macroeconomics, where I analyze how economic policies shape currency valuations to guide my trading decisions. This knowledge provides me with a strategic advantage. My approach to the markets is centered around harmony with their natural momentum.",
      img: Adewumi,
    },
  ];

  const features = [
    "Experts in the main financial markets",
    "Chat 24/7 with our pro traders",
    "Live webinars",
    "Free and VIP Signals",
    "Best forex signals",
    "Full technical analysis",
    "Every question will be answered",
  ];

  return (
    <div>
      {/* Global styles for slick dots */}
      <style>
        {`
          .custom-dots {
            bottom: -40px !important;
          }
          .custom-dots li {
            width: auto !important;
            height: auto !important;
            margin: 0 5px !important;
          }
          .custom-dots li.slick-active div {
            opacity: 1 !important;
            width: 24px !important;
            border-radius: 5px !important;
          }
          .custom-dots li button:before {
            display: none !important;
          }
        `}
      </style>

      {/* Section Header */}
      <Box
        sx={{
          padding: { xs: "20px", sm: "30px", md: "40px" },
          maxWidth: "800px",
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        <Typography
          variant="h4"
          component="h2"
          sx={{
            marginBottom: "16px",
            fontWeight: 700,
            color: "#000000",
            lineHeight: 1.3,
            fontSize: { xs: "1.6rem", sm: "1.9rem", md: "2.2rem" },
          }}
        >
          <span style={{ color: "#0a4fd5" }}>Meet Our</span> Professional
          Traders
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#666666",
            lineHeight: 1.7,
            fontSize: "16px",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          Learn from experienced traders who live and breathe the markets every
          day. Get direct access to their insights, strategies, and mentorship.
        </Typography>
      </Box>

      {/* Main Content */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            width: { xs: "95%", sm: "90%", md: "1100px" },
          }}
        >
          {/* Main Grid Layout */}
          <Grid
            container
            spacing={{ xs: 4, md: 6 }}
            justifyContent="center"
            alignItems="center"
          >
            {/* Left Section - Feature Checklist */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#000000",
                  mb: 3,
                  textAlign: "left",
                }}
              >
                What You Get
              </Typography>
              <List sx={{ p: 0 }}>
                {features.map((text, index) => (
                  <FeatureListItem key={index}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: "#00bfa5", fontSize: 22 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={text}
                      primaryTypographyProps={{
                        style: {
                          fontWeight: 500,
                          color: "#1a1a2e",
                          fontSize: "15px",
                        },
                      }}
                    />
                  </FeatureListItem>
                ))}
              </List>
            </Grid>

            {/* Right Section - Testimonial Carousel */}
            <Grid item xs={12} md={6}>
              <Box sx={{ position: "relative", px: { xs: 2, md: 3 } }}>
                <Slider {...settings}>
                  {traders.map((trader, index) => (
                    <div key={index}>
                      <TraderCard>
                        <CardContent sx={{ p: 0 }}>
                          <Box display="flex" alignItems="center" mb={3}>
                            <StyledAvatar
                              src={trader.img}
                              alt={trader.name}
                            />
                            <Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: "#1a1a2e",
                                  mb: 0.3,
                                }}
                              >
                                {trader.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#0a4fd5",
                                  fontWeight: 600,
                                  fontSize: "14px",
                                }}
                              >
                                {trader.experience}
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              borderLeft: "3px solid #0a4fd5",
                              pl: 2,
                              py: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#4a4a5a",
                                lineHeight: 1.8,
                                fontSize: "14px",
                                fontStyle: "italic",
                              }}
                            >
                              "{trader.testimony}"
                            </Typography>
                          </Box>
                        </CardContent>
                      </TraderCard>
                    </div>
                  ))}
                </Slider>
              </Box>
            </Grid>
          </Grid>

          {/* Button Section */}
          <Box mt={8} textAlign="center">
            <VipButton variant="contained" size="large">
              Become a VIP Member
            </VipButton>
          </Box>
        </Box>
      </div>
    </div>
  );
};

export default ProfessionalTradersSection;